import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Report, type ReportData } from '@/components/report';
import { RecentEvaluations } from '@/components/recent-evaluations';
import {
  submitJob,
  getJob,
  getModels,
  JobStatus,
  jobStatusLabel,
  type JobSubmitRequest,
  type ProblemDetails,
} from '@/lib/api';

const SAMPLE_DESIGN_MD = `# Sample DESIGN.md

## Overview
A clean, minimal page focused on a single primary action.

## Tokens
- Spacing: 4 / 8 / 16 / 24 / 48
- Type scale: 14 / 16 / 20 / 32
- Primary color: electric teal

## Components
- Hero with a one-line value prop and a single CTA button
- Body paragraph (≤ 4 sentences)
- Footer with a single link to source
`;

export function Submit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [url, setUrl] = useState(searchParams.get('url') ?? '');
  const [repoUrl, setRepoUrl] = useState(searchParams.get('repo') ?? '');
  const initialIncludeDesignMd = !searchParams.get('repo'); // if repo provided, default to "let auto-discovery handle it"
  const [includeDesignMd, setIncludeDesignMd] = useState(initialIncludeDesignMd);
  const [designMd, setDesignMd] = useState(SAMPLE_DESIGN_MD);
  const [model, setModel] = useState(searchParams.get('model') ?? 'gpt-4o');
  const [jobId, setJobId] = useState<string | null>(null);

  // Sync state -> URL params (so the form is shareable as a permalink)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (url) params.set('url', url); else params.delete('url');
    if (repoUrl) params.set('repo', repoUrl); else params.delete('repo');
    if (model && model !== 'gpt-4o') params.set('model', model); else params.delete('model');
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, repoUrl, model]);

  const models = useQuery({
    queryKey: ['models'],
    queryFn: getModels,
    staleTime: 5 * 60_000,
  });

  const submit = useMutation({
    mutationFn: (req: JobSubmitRequest) => submitJob(req),
    onSuccess: (data) => setJobId(data.jobId),
  });

  const poll = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const status = q.state.data?.meta.status;
      if (status === JobStatus.Completed || status === JobStatus.Failed) return false;
      return 2000;
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;
    setJobId(null);
    // If the user opted to include DESIGN.md scoring AND has a repo URL but
    // left the textarea blank, send '' for designMd — the evaluator will
    // auto-discover from the repo. If no repo and blank, also '' (skip).
    submit.mutate({
      url,
      designMd: includeDesignMd ? designMd : '',
      sourceUrl: repoUrl.trim() || undefined,
      model,
    });
  };

  const submitProblem = submit.error
    ? ((submit.error as Error & { problem?: ProblemDetails }).problem ?? null)
    : null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-semibold tracking-tight">Evaluate a web app</h1>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          Submit a URL and a DESIGN.md. Heuristic Hero scores it against your design contract,
          Nielsen Norman Group's 10 usability heuristics, and accessibility standards.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New evaluation</CardTitle>
          <CardDescription>
            By submitting you confirm you own the URL or have permission to test it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="url" className="text-sm font-medium">URL</label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="repoUrl" className="text-sm font-medium">
                Source repo <span className="text-[var(--color-muted-foreground)]">(optional)</span>
              </label>
              <Input
                id="repoUrl"
                type="url"
                placeholder="https://github.com/your-org/your-app"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
              />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                If a public GitHub repo is provided we'll shallow-clone it and look for a{' '}
                <code className="font-mono">DESIGN.md</code> at the repo root or under{' '}
                <code className="font-mono">docs/</code>, <code className="font-mono">design/</code>,
                or <code className="font-mono">spec/</code>. Discovered DESIGN.md is used only when
                you don't paste one inline below.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="designMd" className="text-sm font-medium">DESIGN.md</label>
                <label className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                  <input
                    type="checkbox"
                    checked={includeDesignMd}
                    onChange={(e) => setIncludeDesignMd(e.target.checked)}
                    className="accent-[var(--color-primary)]"
                  />
                  Include DESIGN.md alignment scoring
                </label>
              </div>
              {includeDesignMd ? (
                <Textarea
                  id="designMd"
                  rows={12}
                  value={designMd}
                  onChange={(e) => setDesignMd(e.target.value)}
                  className="font-mono text-xs"
                  placeholder={repoUrl ? 'Leave blank to auto-discover DESIGN.md from the repo' : undefined}
                />
              ) : (
                <p className="rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-muted)]/20 px-3 py-3 text-xs text-[var(--color-muted-foreground)]">
                  Skipping DESIGN.md alignment. We'll score on NN/g heuristics (60%) +
                  accessibility (40%) only.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="model" className="text-sm font-medium">Model</label>
              <select
                id="model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="flex h-10 w-full rounded-md border bg-[var(--color-input)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
              >
                {(models.data?.models ?? [{ name: 'gpt-4o', tier: 'default', description: '', isDefault: true, vision: true, reasoning: false }]).map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name} — {m.tier}
                  </option>
                ))}
              </select>
              {models.data && (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {models.data.models.find((m) => m.name === model)?.description}
                </p>
              )}
            </div>

            <Button type="submit" disabled={submit.isPending} className="w-full">
              {submit.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Submit
            </Button>
          </form>

          {submitProblem && (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 p-3 text-sm">
              <div className="font-semibold">{submitProblem.title}</div>
              <div className="text-[var(--color-muted-foreground)]">{submitProblem.detail}</div>
              {submitProblem.traceId && (
                <div className="mt-1 font-mono text-xs opacity-60">trace: {submitProblem.traceId}</div>
              )}
            </div>
          )}

          {jobId && (
            <div className="mt-6 rounded-md border bg-[var(--color-muted)]/30 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-[var(--color-muted-foreground)]">Job</div>
                  <div className="font-mono text-sm">{jobId}</div>
                </div>
                <Badge variant={poll.data?.meta.status === JobStatus.Completed ? 'default' : 'secondary'}>
                  {poll.data ? jobStatusLabel(poll.data.meta.status) : 'Submitting'}
                </Badge>
              </div>
              {poll.data?.meta.status !== undefined && poll.data.meta.status !== JobStatus.Completed && (
                <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                  {poll.data.meta.status === JobStatus.Failed
                    ? `Failed: ${poll.data.meta.error ?? 'unknown error'}`
                    : 'Polling every 2s. Playwright is rendering the page; LLM agents will score it next.'}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {poll.data?.meta.status === JobStatus.Completed && poll.data.response != null && (
        <div className="mt-8">
          <Report data={poll.data.response as unknown as ReportData} />
        </div>
      )}

      <RecentEvaluations />
    </div>
  );
}
